import {
    nextWednesday,
    nextThursday,
    previousWednesday,
    startOfToday,
    subDays,
    endOfDay,
    endOfToday
} from "date-fns";
import { Action, ActionCreator, PayloadAction, Slice, SliceCaseReducers, createSlice } from '@reduxjs/toolkit';
import { BookingInterface, FormType, deliveryOptionsInterface } from '../types/types';
import { addDays, calculateMinPlayStart, trySoonerStartDate, isTransforceDelivery, endDateFromReturnDate, newStateFromFlatBooking, startDateFromDeliveryDate, validHomeAddress } from '../components/utils/stateHelper';
import { getBookingConfig, getRentalPointDelivery } from '../data/price';
import { DeliveryManager } from '../data/DeliveryManager';
import { changeGunOptions, setBookingStateFromFlat } from './gunOptionSlice';
import { DPDDeliveryService } from '../api/DPDDeliveryService';
import { TFDeliveryService } from '../api/TFDeliveryService';
import { min } from 'lodash';
import { DeliveryService } from '../api/DeliveryService';
import { promoPlayRange } from "../api/PromotionService";
export interface DeliverySliceInterface extends deliveryOptionsInterface {
    formType: FormType,
    minPlayStart?: Date,
    minDeliveryDate?: Date,
    maxDeliveryDate?: Date,
    minReturnDate?: Date,
    maxReturnDate?: Date
    homeAddressValid?: Boolean,
    activePromo: string | null
}
export const deliverySlice: Slice<DeliverySliceInterface> = createSlice<DeliverySliceInterface, SliceCaseReducers<DeliverySliceInterface>>({
    name: 'delivery',
    initialState: {
        playStart: null,
        playEnd: null,
        deliveryType: "",
        deliveryDate: undefined,
        returnDate: undefined,
        rentalPoint: undefined,
        homeAddress: undefined,
        homeAddressValid: undefined,
        building: "house",
        buildingDetails: undefined,
        transforceComments: undefined,
        insuranceAddress: undefined,
        dpdPoint: null,
        dpdSearchAddress: "",
        startDate: undefined,
        endDate: undefined,
        minPlayStart: undefined,
        minDeliveryDate: undefined,
        maxDeliveryDate: undefined,
        minReturnDate: undefined,
        maxReturnDate: undefined,
        formType: "Default" as FormType,
        activePromo: null
    },
    reducers: {
        /**
         * Changes the options given. Don't use this when there's a more specific option available
         * to avoid skipping business logic.
         */
        changeDeliveryOptions: (state, action: PayloadAction<DeliverySliceInterface>) => {
            return {
                ...state,
                ...action.payload
            };
        },

        // For YouthPromo, range=3
        /**
         * Updates `deliveryOptions` with a new `playStart` value. Clears previous, now-invalid fields. Calculates a new
         * `playEnd` value based on `range`. If `deliveryType` is a DPD point delivery, calculates a new `startDate`.
         * 
         * Wraps params in an object
         * 
         * @param playStart - The start of the playing range
         * @param range - The number of days that the play range should span
         */
        setPlayRange: (state, action: PayloadAction<{playStart: Date, range: number}>) => {
            const playStart: Date | null = action.payload.playStart;
            const range: number = action.payload.range;

            state.playStart = playStart;
            state.playEnd = addDays(playStart, range - 1);
            // Clear fields that depended on previous play info
            state.deliveryDate = undefined;
            state.returnDate = undefined;
            state.deliveryType = null;
            // Calculate the startDate again if applicable. Else, make sure it's undefined
            const bookingConfig = getBookingConfig();
            const manager = new DeliveryManager(bookingConfig);    
            if (state.startDate && manager.isDPDPointDelivery(state.deliveryType || '') && playStart) {
                state.startDate = DPDDeliveryService.latestShippingDateBefore(playStart);
            }else {
                state.startDate = undefined;
            }
            state.endDate = undefined;
        },

        /**
         * Modifies the delivery options using business logic based on the new delivery type
         * 
         * @param type (string) - Can be passed as normal
         */
        setDeliveryType: (state, action: PayloadAction<string>) => {
            const type: string = action.payload;

            const bookingConfig = getBookingConfig();
            const manager = new DeliveryManager(bookingConfig);
            // logError('setDeliveryType ' + type, deliveryOptions);
            if (manager.isDPDPointDelivery(type) && state.playStart && state.playEnd) {
                state.deliveryType = type;
                state.rentalPoint = undefined;
                state.endDate = undefined;
                state.returnDate = undefined;
                state.deliveryDate = undefined;
                
                state.startDate = DPDDeliveryService.latestShippingDateBefore(state.playStart);
                
                // min and max delivery not relevant because
                // that's not user-defined
                
                state.minReturnDate = DPDDeliveryService.earliestReturnDateAfter(state.playEnd);
                if (state.formType === 'YouthPromo' || state.activePromo === 'Kerst vakantie') {
                    state.maxReturnDate = state.minReturnDate;
                } else {
                    state.maxReturnDate = nextWednesday(state.playEnd);
                }
            } else if (manager.isTransforceDelivery(type) && state.playStart && state.playEnd) {
                state.deliveryType = type;
                state.rentalPoint = undefined;
                state.endDate = undefined;
                state.returnDate = undefined;
                state.deliveryDate = undefined;
                state.startDate = undefined;

                ////////////////////////////////////////////
                // Calculate min/max delivery/return dates
                ////////////////////////////////////////////
                // Calculate max delivery date
                let max = new Date(state.playStart);
                max = subDays(max, 1);
                // Find first working date before playStart
                while (!TFDeliveryService.isWorkingDate(max)) {
                    max = subDays(max, 1);
                }
                const today = startOfToday();
                if (today >= max) {
                    state.maxDeliveryDate = undefined;
                    state.minDeliveryDate = undefined;
                } else {
                    state.maxDeliveryDate = max;
                }

                // Calculate min delivery date
                // if (state.formType === 'YouthPromo') {
                if (state.formType === 'YouthPromo' || state.activePromo === 'Kerst vakantie') {
                    state.minDeliveryDate = state.maxDeliveryDate;
                } else {
                    //The default delivery (start date) of a Transforce booking is Wednesday, however the customer can edit this.
                    let minDeliveryDate = previousWednesday(state.playStart);
                    if (today >= minDeliveryDate) {
                        minDeliveryDate = DeliveryService.addBusinessDays(today, 2);
                    }
                    state.minDeliveryDate = minDeliveryDate
                }

                // Calculate min return date
                state.minReturnDate = DeliveryService.addBusinessDays(state.playEnd, 1);

                // Calculate max return date
                // if (state.formType === 'YouthPromo') {
                if (state.formType === 'YouthPromo' || state.activePromo === 'Kerst vakantie') {
                    state.maxReturnDate = state.minReturnDate;
                } else {
                    state.maxReturnDate = nextThursday(state.playEnd);
                }
            } else {
                state.deliveryType = type;
                state.rentalPoint = undefined;
                state.startDate = undefined;
                state.endDate = undefined;
                state.returnDate = undefined;
                state.deliveryDate = undefined;
            }
        },

        /**
         * Can only be filled if playRange has been called and if setDeliveryType has been called
         */
        autofillDeliveryReturn(state) {
            if (state.playStart && state.deliveryType) {
                state.deliveryDate = state.maxDeliveryDate!;
                state.startDate = startDateFromDeliveryDate(state.deliveryDate, state.playStart, state.deliveryType);
                state.returnDate = state.minReturnDate!;
                state.endDate = endDateFromReturnDate(state.returnDate, state.deliveryType);
                if (isTransforceDelivery(state.deliveryType)) {
                    state.startDate = trySoonerStartDate(state.startDate);
                    // The default delivery date is one day after the start date. If the start date
                    // was successfully moved back, this means that there is one buffer day between
                    // the delivery date and the play start date
                    state.deliveryDate = DeliveryService.addBusinessDays(state.startDate, 1);
                    let newMinDeliveryDate;
                    if (state.activePromo === 'Kerst vakantie') {
                        // For the Christmas vacation promotion, make the minimum delivery date the latest
                        // day that has a buffer day
                        // At this point in the code, that's `deliveryDate`
                        newMinDeliveryDate = state.deliveryDate;
                    } else {
                        // Else, set the minimum delivery date to ASAP. Later, we move that up
                        newMinDeliveryDate = TFDeliveryService.earliestDeliveryDateAfter(new Date());
                    }

                    // While `newMinDeliveryDate` is too early, keep moving it forward
                    // Result:
                    //   Either:
                    //     The min delivery date will already be really close to the default
                    //     delivery date and there is no possible margin
                    //       OR
                    //     The min delivery date will be 2 days before the default delivery date.
                    while (
                        endOfDay(newMinDeliveryDate)
                            <= endOfDay(DeliveryService
                                .subtractBusinessDays(state.deliveryDate, 2))
                    ) {
                        newMinDeliveryDate = DeliveryService.addBusinessDays(newMinDeliveryDate, 1);
                    }
                    state.minDeliveryDate = newMinDeliveryDate;
                }

            }
        },

        /**
         * Sets the delivery date and sets a corresponding start date
         * 
         * @param newDeliveryDate (Date) - Pass without object 
         */
        setDeliveryDate(state, action: PayloadAction<Date>) {
            const newDeliveryDate: Date = action.payload;

            // If playStart and deliveryType aren't set, then something is wrong and the state
            // shouldn't change.
            if (state.playStart && state.deliveryType) {
                state.deliveryDate = newDeliveryDate;
                state.startDate = startDateFromDeliveryDate(
                    newDeliveryDate,
                    state.playStart,
                    state.deliveryType
                );
            }
        },

        /**
         * Sets the return date and sets a corresponding end date
         * 
         * @param newReturnDate (Date) - Pass without object 
         */
        setReturnDate(state, action: PayloadAction<Date>) {
            const newReturnDate: Date = action.payload;
            if (newReturnDate && state.deliveryType) {
                state.returnDate = newReturnDate;
                state.endDate = endDateFromReturnDate(newReturnDate, state.deliveryType)
            }
        },

        /**
         * Sets rental point to given rental point number and updates delivery options
         * to match.
         * 
         * @param rentalPoint (number) - Pass without object
         */
        setRentalPoint: (state, action: PayloadAction<number>) => {
            const rentalPoint = action.payload;
            state.rentalPoint = rentalPoint;
            state.deliveryType = 'rental';
            state.startDate = undefined;
            state.endDate = undefined;
            state.returnDate = undefined;
            state.deliveryDate = undefined;
        },
        /**
         * Country already belongs to the invoice slice, so it must be passed as a parameter for
         * this slice. Combining them is an option to avoid passing it as a parameter, but I've
         * opted to avoid this for now to separate the logic and namespace.
         */
        setHomeAddress: (state, action: PayloadAction<{homeAddress: string | undefined, country: string}>) => {
            const homeAddress = action.payload.homeAddress;
            const country = action.payload.country;
            state.homeAddress = homeAddress;
            if (homeAddress) {
                state.homeAddressValid = validHomeAddress(homeAddress, country);
            }
        },

        resetDeliveryOptions: (state) => {
            const init = deliverySlice.getInitialState();
            return {
                ...init,
                minPlayStart: state.minPlayStart,
                formType: state.formType,
                activePromo: state.activePromo
            };
        },
        
        /**
         * Input string for FormType
         */
        setFormType: (state, action) => {
            state.formType = action.payload;
        },

        setDPDAddress: (state, action) => {
            const dpdAddress = action.payload;
            state.dpdSearchAddress = dpdAddress;
        },
        /**
         * To reset to null, provide an empty string. Take care that setPlayRange gets called again. Else, they may keep
         * things they're not supposed to (e.g. 20 euro shipping when the promo was free shipping)
         */
        setActivePromotion: (state, action) => {
            const promotionName: string | null = action.payload;
            if (promotionName === "") {
                state.activePromo = null;
            } else {
                state.activePromo = promotionName;
            }
        },
        /**
         * Must be called in between the form actually loading and step 2's calendar showing. Country can get stuck
         * in the in-page cache, and running this immediately can cause the wrong 'country' value to be used.
         */
        initializeMinPlayStart: (state) => {
            state.minPlayStart = calculateMinPlayStart();
        },
    },
    extraReducers(builders) {
        builders.addCase(setBookingStateFromFlat, (state, action) => {
            const newState = newStateFromFlatBooking(state, action.payload);
            return newState as DeliverySliceInterface;
        })
        .addCase(changeGunOptions, (state, action) => {
            const init = deliverySlice.getInitialState();
            return {
                ...init,
                minPlayStart: state.minPlayStart,
                formType: state.formType,
                activePromo: state.activePromo
            };
        });
    },
});

export const {
    changeDeliveryOptions,
    setPlayRange,
    setDeliveryType,
    setRentalPoint,
    resetDeliveryOptions,
    autofillDeliveryReturn,
    setDeliveryDate,
    setReturnDate,
    setFormType,
    setHomeAddress,
    setDPDAddress,
    setActivePromotion,
    initializeMinPlayStart,
} = deliverySlice.actions;
export default deliverySlice.reducer;